import { useState, useEffect, useRef } from 'react';
import { staffService } from '../services';

/**
 * Is this email address already in use?
 *
 * Asked as the manager finishes typing, so "already in use" is answered beside
 * the box rather than by a failed save several steps later — which on the Add
 * Staff form means walking back through three steps of a wizard to find out.
 *
 * DEBOUNCED, not on every keystroke: a request per character would ask about
 * "j", "jo", "joh"… and answer questions nobody asked. It waits for typing to
 * stop, and only asks about something that looks like an address at all.
 *
 * The answer is advisory. The save still enforces uniqueness — this is a
 * courtesy, and a check that cannot run (offline, an older API) must never stop
 * somebody filling a form in. That is why a failure reads as "unknown" rather
 * than as "taken".
 *
 * @param {string} email       what is in the box
 * @param {number} excludeId   a staff id being edited, so their own address is
 *                             still theirs to keep
 * @returns {{ state: 'idle'|'checking'|'free'|'taken', message: string }}
 */
const useEmailAvailability = (email, { excludeId = null, delay = 600 } = {}) => {
  const [result, setResult] = useState({ state: 'idle', message: '' });
  // Only the newest answer is allowed to land: a slow reply about an address
  // the manager has since corrected would contradict the box they are looking at.
  const latest = useRef(0);

  useEffect(() => {
    const value = String(email || '').trim();
    latest.current += 1;
    const mine = latest.current;

    if (!value || !/^\S+@\S+\.\S+$/.test(value)) {
      setResult({ state: 'idle', message: '' });
      return undefined;
    }

    setResult({ state: 'checking', message: '' });

    const timer = setTimeout(async () => {
      try {
        const response = await staffService.checkEmailAvailable(value, excludeId);
        if (mine !== latest.current) return;
        const data = response?.data || {};
        if (!data.checked) {
          setResult({ state: 'idle', message: '' });
        } else if (data.available) {
          setResult({ state: 'free', message: '' });
        } else {
          setResult({ state: 'taken', message: data.message || 'This email address is already in use.' });
        }
      } catch {
        // Unknown, not taken — see above.
        if (mine === latest.current) setResult({ state: 'idle', message: '' });
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [email, excludeId, delay]);

  return result;
};

export default useEmailAvailability;
